export type GameState = 'idle' | 'playing' | 'gameover'

export interface GameEngineOptions {
  canvas: HTMLCanvasElement
  onScoreUpdate: (score: number) => void
  onGameOver: () => void
}

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  maxLife: number
  size: number
  color: string
}

interface Obstacle {
  type: 'spike' | 'block' | 'gate'
  x: number
  w: number
  h: number
  scale: number
  gapTop?: number
  gapBottom?: number
}

interface Building {
  x: number
  w: number
  h: number
}

// Config
const PLAYER_COLOR = '#C6FF00'
const BASE_SPEED = 400
const GRAVITY = 2500
const JUMP_VEL = -850
const MAX_JUMPS = 2
const SKYLINE_COLOR = '#252525'
const OBSTACLE_COLOR = '#8A8F98'
const PARTICLE_COUNT = 30

export class GameEngine {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private onScoreUpdate: (score: number) => void
  private onGameOver: () => void

  private state: GameState = 'idle'
  private animId = 0
  private lastTime = 0
  private w = 0
  private h = 0
  private groundY = 0

  private player = { x: 0, y: 0, vy: 0, radius: 12, jumpCount: 0, idleTime: 0 }
  private speed = 0
  private distance = 0
  private score = 0
  private spawnTimer = 0
  private nextSpawn = 1.5

  private buildings: Building[] = []
  private skyOff = 0
  private ambient: Particle[] = []
  private trail: Particle[] = []
  private burst: Particle[] = []
  private obstacles: Obstacle[] = []

  constructor(opts: GameEngineOptions) {
    this.canvas = opts.canvas
    this.ctx = this.canvas.getContext('2d')!
    this.onScoreUpdate = opts.onScoreUpdate
    this.onGameOver = opts.onGameOver

    this._resize = this._resize.bind(this)
    window.addEventListener('resize', this._resize)
    this._resize()
    this._initSkyline()
    this._initAmbient()
    this._resetPlayer()

    this.lastTime = performance.now()
    this._loop(this.lastTime)
  }

  private _resize() {
    const parent = this.canvas.parentElement
    if (!parent) return
    const rect = parent.getBoundingClientRect()
    if (rect.width === 0) return
    const dpr = window.devicePixelRatio || 1
    this.canvas.width = rect.width * dpr
    this.canvas.height = rect.height * dpr
    this.w = this.canvas.width
    this.h = this.canvas.height
    this.groundY = this.h * 0.85
    this.player.x = this.w * 0.2
    if (this.state === 'idle') this.player.y = this.groundY - this.player.radius
  }

  private _initSkyline() {
    this.buildings = []
    let x = 0
    while (x < this.w * 2 + 1000) {
      const bw = 40 + Math.random() * 80
      const bh = 50 + Math.random() * 150
      this.buildings.push({ x, w: bw, h: bh })
      x += bw + 10 + Math.random() * 30
    }
  }

  private _initAmbient() {
    this.ambient = []
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      this.ambient.push({
        x: Math.random() * this.w,
        y: Math.random() * this.h,
        vx: -10 - Math.random() * 20,
        vy: -5 - Math.random() * 10,
        life: 1,
        maxLife: 1,
        size: 1 + Math.random() * 1.5,
        color: 'rgba(255,255,255,0.15)',
      })
    }
  }

  private _resetPlayer() {
    this.player.y = this.groundY - this.player.radius
    this.player.vy = 0
    this.player.jumpCount = 0
    this.player.idleTime = 0
  }

  public start() {
    this.state = 'playing'
    this._resetPlayer()
    this.speed = BASE_SPEED
    this.distance = 0
    this.score = 0
    this.obstacles = []
    this.trail = []
    this.burst = []
    this.spawnTimer = 0
    this.nextSpawn = 1.5
    this.onScoreUpdate(0)
  }

  public jump() {
    if (this.state === 'idle') {
      this.start()
      this._doJump()
    } else if (this.state === 'playing' && this.player.jumpCount < MAX_JUMPS) {
      this._doJump()
    }
  }

  private _doJump() {
    this.player.vy = JUMP_VEL
    this.player.jumpCount++
    for (let i = 0; i < 10; i++) {
      this.burst.push({
        x: this.player.x,
        y: this.player.y + this.player.radius,
        vx: (Math.random() - 0.5) * 200,
        vy: -20 - Math.random() * 50,
        life: 0.5 + Math.random() * 0.5,
        maxLife: 1,
        size: 2 + Math.random() * 3,
        color: PLAYER_COLOR,
      })
    }
  }

  private _explode() {
    for (let i = 0; i < 30; i++) {
      this.burst.push({
        x: this.player.x,
        y: this.player.y,
        vx: (Math.random() - 0.5) * 800,
        vy: (Math.random() - 0.5) * 800,
        life: 1 + Math.random(),
        maxLife: 2,
        size: 2 + Math.random() * 4,
        color: PLAYER_COLOR,
      })
    }
  }

  private _spawnObs() {
    const types: ('spike' | 'block')[] = ['spike', 'block']
    if (this.score >= 200) types.push('block') // could add gate later
    const type = types[Math.floor(Math.random() * types.length)]
    let ow = 30,
      oh = 40
    if (type === 'block') {
      ow = 40
      oh = 40 + Math.random() * 20
    }
    this.obstacles.push({ type, x: this.w + 50, w: ow, h: oh, scale: 0 })
  }

  private _collides(): boolean {
    const pr = this.player.radius * 0.8
    const px = this.player.x,
      py = this.player.y
    for (const o of this.obstacles) {
      if (o.scale < 0.5) continue
      if (o.type === 'spike') {
        const ox = o.x - o.w * 0.3,
          oy = this.groundY - o.h * 0.7
        const ow = o.w * 0.6,
          oh = o.h * 0.7
        if (
          px + pr > ox &&
          px - pr < ox + ow &&
          py + pr > oy &&
          py - pr < oy + oh
        )
          return true
      } else {
        const ox = o.x - o.w / 2,
          oy = this.groundY - o.h
        if (
          px + pr > ox &&
          px - pr < ox + o.w &&
          py + pr > oy &&
          py - pr < oy + o.h
        )
          return true
      }
    }
    return false
  }

  private _update(dt: number) {
    // Ambient
    for (const p of this.ambient) {
      p.x += p.vx * dt
      p.y += p.vy * dt
      if (p.x < -10) p.x = this.w + 10
      if (p.y < -10) p.y = this.h + 10
    }

    if (this.state === 'idle') {
      this.player.idleTime += dt
      this.player.y =
        this.groundY -
        this.player.radius +
        Math.sin(this.player.idleTime * 4) * 5
      return
    }

    if (this.state !== 'playing') return

    // Physics
    this.player.vy += GRAVITY * dt
    this.player.y += this.player.vy * dt
    if (this.player.y >= this.groundY - this.player.radius) {
      this.player.y = this.groundY - this.player.radius
      this.player.vy = 0
      this.player.jumpCount = 0
    }

    // Speed & Score
    this.speed = BASE_SPEED + this.score * 0.5
    this.distance += this.speed * dt
    const ns = Math.floor(this.distance / 40)
    if (ns !== this.score) {
      this.score = ns
      this.onScoreUpdate(ns)
    }

    // Skyline scroll
    this.skyOff += this.speed * 0.3 * dt
    if (this.skyOff > 1000) {
      this.skyOff -= 1000
      this.buildings.forEach((b) => (b.x -= 1000))
      let last = this.buildings[this.buildings.length - 1]
      let lx = last.x + last.w
      while (lx < this.w * 2 + 1000) {
        const bw = 40 + Math.random() * 80
        const bh = 50 + Math.random() * 150
        this.buildings.push({ x: lx + 10 + Math.random() * 30, w: bw, h: bh })
        last = this.buildings[this.buildings.length - 1]
        lx = last.x + last.w
      }
      this.buildings = this.buildings.filter((b) => b.x + b.w > -1000)
    }

    // Obstacles
    this.spawnTimer += dt
    if (this.spawnTimer >= this.nextSpawn) {
      this._spawnObs()
      this.spawnTimer = 0
      this.nextSpawn =
        Math.max(0.6, 1.5 - (this.speed - BASE_SPEED) / 1000) +
        Math.random() * 0.8
    }
    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      const o = this.obstacles[i]
      o.x -= this.speed * dt
      if (o.scale < 1) o.scale = Math.min(1, o.scale + dt * 5)
      if (o.x < -100) this.obstacles.splice(i, 1)
    }

    // Trail
    if (
      Math.random() < 0.3 ||
      this.player.y < this.groundY - this.player.radius
    ) {
      this.trail.push({
        x: this.player.x - this.player.radius + Math.random() * 5,
        y: this.player.y + (Math.random() - 0.5) * 10,
        vx: -this.speed * 0.2,
        vy: (Math.random() - 0.5) * 40,
        life: 0.3 + Math.random() * 0.4,
        maxLife: 0.7,
        size: 2 + Math.random() * 3,
        color: PLAYER_COLOR,
      })
    }

    // Collision
    if (this._collides()) {
      this.state = 'gameover'
      this._explode()
      this.onGameOver()
    }
  }

  private _render() {
    const ctx = this.ctx
    ctx.clearRect(0, 0, this.w, this.h)

    // Skyline
    ctx.fillStyle = SKYLINE_COLOR
    for (const b of this.buildings) {
      const bx = b.x - this.skyOff
      if (bx + b.w > 0 && bx < this.w)
        ctx.fillRect(bx, this.groundY - b.h, b.w, b.h)
    }

    // Ambient
    for (const p of this.ambient) {
      ctx.fillStyle = p.color
      ctx.beginPath()
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
      ctx.fill()
    }

    // Ground
    ctx.save()
    ctx.beginPath()
    ctx.moveTo(0, this.groundY)
    ctx.lineTo(this.w, this.groundY)
    ctx.strokeStyle = '#2F2F2F'
    ctx.lineWidth = 2 * (window.devicePixelRatio || 1)
    ctx.shadowBlur = 15
    ctx.shadowColor = 'rgba(47,47,47,0.5)'
    ctx.stroke()
    ctx.restore()

    // Obstacles
    ctx.fillStyle = OBSTACLE_COLOR
    for (const o of this.obstacles) {
      ctx.save()
      ctx.translate(o.x, this.groundY)
      ctx.scale(o.scale, o.scale)
      if (o.type === 'spike') {
        ctx.beginPath()
        ctx.moveTo(0, -o.h)
        ctx.lineTo(o.w / 2, 0)
        ctx.lineTo(-o.w / 2, 0)
        ctx.fill()
      } else {
        ctx.fillRect(-o.w / 2, -o.h, o.w, o.h)
      }
      ctx.restore()
    }

    // Particles helper
    const drawP = (arr: Particle[], glow: boolean) => {
      for (const p of arr) {
        ctx.save()
        ctx.globalAlpha = Math.max(0, p.life / p.maxLife)
        if (glow) {
          ctx.shadowBlur = 10
          ctx.shadowColor = p.color
        }
        ctx.fillStyle = p.color
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.size * (p.life / p.maxLife), 0, Math.PI * 2)
        ctx.fill()
        ctx.restore()
      }
    }
    drawP(this.trail, false)
    drawP(this.burst, true)

    // Player
    if (this.state !== 'gameover') {
      ctx.save()
      ctx.fillStyle = PLAYER_COLOR
      ctx.shadowBlur = 20
      ctx.shadowColor = PLAYER_COLOR + '59'
      ctx.beginPath()
      ctx.arc(this.player.x, this.player.y, this.player.radius, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()
    }
  }

  private _loop = (time: number) => {
    const dt = Math.min((time - this.lastTime) / 1000, 0.1)
    this.lastTime = time
    this._update(dt)
    this._render()

    // Update particles
    const upd = (arr: Particle[]) => {
      for (let i = arr.length - 1; i >= 0; i--) {
        arr[i].x += arr[i].vx * dt
        arr[i].y += arr[i].vy * dt
        arr[i].life -= dt
        if (arr[i].life <= 0) arr.splice(i, 1)
      }
    }
    upd(this.trail)
    upd(this.burst)

    this.animId = requestAnimationFrame(this._loop)
  }

  public destroy() {
    window.removeEventListener('resize', this._resize)
    cancelAnimationFrame(this.animId)
  }
}
